import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { IntegrationProvider } from './useGetIntegrations';

export interface IntegrationCredentialPayload {
    _id?: string;
    locationId?: string;
    username: string;
    password?: string;
}

export interface UpsertIntegrationVariables {
    provider: IntegrationProvider;
    credentials: IntegrationCredentialPayload[];
}

export interface DeleteIntegrationVariables {
    provider: IntegrationProvider;
    /** SSM parameter name returned as `_id` from `useGetIntegrations` */
    deleteFilter: { _id: string };
}

export interface UpsertNexIntegrationVariables {
    nexApiCustomerGuid: string;
}

const upsertIntegration = async (variables: UpsertIntegrationVariables, sessionId: string) => {
    const { data } = await axios.post(
        `${API_URL}/settings/account/integrations`,
        { action: 'upsert', ...variables },
        { headers: { Authorization: sessionId } },
    );
    return data;
};

const upsertNexIntegration = async (variables: UpsertNexIntegrationVariables, sessionId: string) => {
    const { data } = await axios.post(
        `${API_URL}/settings/account/integrations`,
        { action: 'upsert', provider: 'nex', ...variables },
        { headers: { Authorization: sessionId } },
    );
    return data;
};

const deleteIntegration = async (variables: DeleteIntegrationVariables, sessionId: string) => {
    const { data } = await axios.post(
        `${API_URL}/settings/account/integrations`,
        { action: 'delete', ...variables },
        { headers: { Authorization: sessionId } },
    );
    return data;
};

export const useUpsertIntegration = (
    options?: UseMutationOptions<unknown, Error, UpsertIntegrationVariables>,
) => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (variables: UpsertIntegrationVariables) =>
            upsertIntegration(variables, sessionContext?.sessionId ?? ''),
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: ['integrations'] });
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

export const useUpsertNexIntegration = (
    options?: UseMutationOptions<unknown, Error, UpsertNexIntegrationVariables>,
) => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (variables: UpsertNexIntegrationVariables) =>
            upsertNexIntegration(variables, sessionContext?.sessionId ?? ''),
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: ['integrations'] });
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

export const useDeleteIntegration = (
    options?: UseMutationOptions<unknown, Error, DeleteIntegrationVariables>,
) => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (variables: DeleteIntegrationVariables) =>
            deleteIntegration(variables, sessionContext?.sessionId ?? ''),
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: ['integrations'] });
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};
